import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, AlertCircle, Lock } from "lucide-react";
import logo from "@/assets/logo.png";
import extractErrorMessage from "@/utils/extractErrorMessage";
import { getBackendUrl } from "@/utils/apiBase";

const API = `${getBackendUrl()}/api`;
const ROSE = "#b8977e";

const BalancePaymentPage = () => {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [paid, setPaid] = useState(false);

  const fetchLead = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/quotes/${token}`);
      setLead(r.data);
      if (r.data.balance_paid) setPaid(true);
    } catch (e) {
      setError(extractErrorMessage(e, "Réservation introuvable"));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchLead(); }, [fetchLead]);

  // Detect Stripe redirect → verify balance status
  useEffect(() => {
    const paidParam = searchParams.get("paid");
    const sessionId = searchParams.get("session_id");
    if (paidParam === "true" && sessionId && !verifying) {
      setVerifying(true);
      axios.get(`${API}/checkout/balance-status/${sessionId}`)
        .then((r) => {
          if (r.data?.paid) setPaid(true);
          searchParams.delete("paid");
          searchParams.delete("session_id");
          setSearchParams(searchParams, { replace: true });
          fetchLead();
        })
        .catch(() => {})
        .finally(() => setVerifying(false));
    }
  }, [searchParams, setSearchParams, fetchLead, verifying]);

  const handlePay = async () => {
    setPaying(true);
    try {
      const res = await axios.post(`${API}/quotes/${token}/create-balance-checkout`);
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (e) {
      alert(extractErrorMessage(e, "Erreur lors du paiement"));
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#faf8f5] flex items-center justify-center font-['DM_Sans']">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#b8977e] mx-auto mb-4" />
          <p className="text-[#666]">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#faf8f5] flex items-center justify-center font-['DM_Sans']">
        <div className="text-center max-w-md px-6">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#1a1a1a] mb-2 font-['Playfair_Display']">Réservation introuvable</h1>
          <p className="text-[#666] mb-6">{error}</p>
          <Link to="/" className="inline-block bg-[#b8977e] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#a68770] transition-colors">Retour</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf8f5] font-['DM_Sans']" data-testid="balance-page">
      <nav className="bg-white shadow-sm">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center">
          <Link to="/"><img src={logo} alt="SelfieBooth Event" style={{ height: 48, width: "auto" }} /></Link>
        </div>
      </nav>

      <div className="max-w-xl mx-auto px-6 py-10">
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1a1a1a] font-['Playfair_Display']" data-testid="balance-title">
            {paid ? "✓ Paiement confirmé" : "Paiement du solde"}
          </h1>
          <p className="text-sm text-[#999] mt-2">SelfieBooth Event</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-[#e8d5c4] p-6 sm:p-7 mb-6" data-testid="balance-recap">
          <h3 className="text-base font-bold text-[#b8977e] font-['Playfair_Display'] mb-4">Votre réservation</h3>
          <div className="space-y-2 text-sm">
            {lead?.package?.name && (
              <div className="flex justify-between"><span className="text-[#5a5a5a]">Forfait</span><span className="font-semibold">{lead.package.name}</span></div>
            )}
            {lead?.event_date && (
              <div className="flex justify-between"><span className="text-[#5a5a5a]">Date</span><span className="font-semibold">{lead.event_date}</span></div>
            )}
            {lead?.event_location && (
              <div className="flex justify-between"><span className="text-[#5a5a5a]">Lieu</span><span className="font-semibold">{lead.event_location}</span></div>
            )}
          </div>
          <div className="border-t border-[#e8d5c4] mt-4 pt-3 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-[#999]">Total réservation</span><span>{lead?.total_price?.toFixed(2)} €</span></div>
            <div className="flex justify-between text-[#2d7a4f]"><span>Acompte réglé (30%) ✓</span><span>-{lead?.deposit_amount?.toFixed(2)} €</span></div>
            <div className="flex justify-between items-baseline pt-3 border-t border-[#e8d5c4]">
              <span className="font-bold font-['Playfair_Display']">Solde restant (70%)</span>
              <span className="text-2xl font-bold text-[#b8977e]" data-testid="balance-amount">{lead?.balance_amount?.toFixed(2)} €</span>
            </div>
          </div>
        </div>

        {paid && (
          <div className="bg-[#f0fdf4] border border-[#86efac] rounded-2xl p-8 text-center" data-testid="balance-success">
            <div className="flex items-center justify-center mb-3"><CheckCircle2 className="w-14 h-14 text-[#2d7a4f]" /></div>
            <h2 className="text-xl font-bold text-[#2d7a4f] font-['Playfair_Display'] mb-1">Solde réglé !</h2>
            <p className="text-sm text-[#5a5a5a] mb-2">Votre réservation est intégralement payée.<br/>Vous recevrez votre facture finale par email.</p>
            <p className="text-[#b8977e] font-semibold font-['Playfair_Display'] mt-4">À très bientôt pour votre événement 🎉</p>
          </div>
        )}

        {!paid && (
          <div>
            <button
              onClick={handlePay}
              disabled={paying || verifying}
              className="w-full font-semibold text-white py-4 rounded-full text-base transition-all disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              style={{
                background: paying || verifying ? "#ccc" : `linear-gradient(135deg, ${ROSE} 0%, #a68770 100%)`,
                boxShadow: !paying && !verifying ? "0 4px 20px rgba(184,151,126,0.3)" : "none",
              }}
              data-testid="pay-balance-btn"
            >
              {verifying ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Vérification...</>
              ) : paying ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Redirection vers Stripe...</>
              ) : (
                <><Lock className="w-4 h-4" /> Régler le solde {lead?.balance_amount?.toFixed(2)} €</>
              )}
            </button>
            <p className="text-center text-xs text-[#999] mt-2">Paiement sécurisé par Stripe · Visa / Mastercard / CB</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BalancePaymentPage;
